import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { isoDateInZone } from '@hp/domain';
import { HpButton, PageHeading, Segmented } from '@hp/ui';
import { ErrorAlert } from '../../../components/error-alert';
import { fmtDateTime } from '../../../lib/format';
import { BudgetCard } from './budget-card';
import { CampaignTable } from './campaign-table';
import { ChannelQuality } from './channel-quality';
import { ChannelTable } from './channel-table';
import { Economics } from './economics';
import { FilterBar } from './filter-bar';
import { defaultFilter, isFilterActive, VIEWS, type DashboardFilter, type View } from './filters';

/** Section anchors of the report; jumping to one switches to the view that holds it. */
const SECTIONS = [
  { id: 'pa-overview', view: 'manager' },
  { id: 'pa-economics', view: 'manager' },
  { id: 'pa-funnel', view: 'manager' },
  { id: 'pa-finance', view: 'manager' },
  { id: 'pa-campaigns', view: 'marketing' },
  { id: 'pa-data-quality', view: 'marketing' },
] as const satisfies ReadonlyArray<{ id: string; view: View }>;
import { makeFmt } from './format';
import { DataQuality } from './data-quality';
import { Funnel } from './funnel';
import { Insights } from './insights';
import { KpiGrid } from './kpi-grid';
import { TrendChart } from './trend-chart';
import { useFilterOptions, useReport } from './use-report';

/** The two-view campaign performance dashboard (HPD-16 management, HPD-27 marketing) over the published snapshot. */
export function Dashboard() {
  const { t } = useTranslation(['performance', 'common']);
  const navigate = useNavigate();
  const fmt = useMemo(() => makeFmt(t), [t]);
  const today = isoDateInZone(new Date());
  const [view, setView] = useState<View>('manager');
  const [filter, setFilter] = useState<DashboardFilter>(() => defaultFilter(today));
  const valid = filter.from <= filter.to && filter.to <= today;
  const options = useFilterOptions();
  const report = useReport(
    {
      from: filter.from,
      to: filter.to,
      compare: filter.compare,
      channels: filter.channels.length ? (filter.channels as never) : undefined,
      projects: filter.projects.length ? filter.projects : undefined,
      campaigns: filter.campaigns.length ? filter.campaigns : undefined,
    },
    valid,
  );
  const data = report.data;
  const navRef = useRef<HTMLElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  // The section bar sticks under the topbar once the page scrolls past its anchor (the prototype's behaviour).
  useEffect(() => {
    const nav = navRef.current;
    const anchor = anchorRef.current;
    if (!nav || !anchor) return;
    const topbar = document.querySelector('.topbar');
    const edge = topbar ? topbar.getBoundingClientRect().bottom : 0;
    const observer = new IntersectionObserver(
      () => nav.classList.toggle('is-stuck', anchor.getBoundingClientRect().top <= edge),
      { rootMargin: `-${edge}px 0px 0px 0px`, threshold: [0, 1] },
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="performance-workspace bo-sections">
      <PageHeading
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        actions={
          <span className="bo-row">
            <Segmented
              className="pa-view-switch"
              ariaLabel={t('dashboard.view.aria')}
              value={view}
              options={VIEWS.map((v) => ({ value: v, label: t(`dashboard.view.${v}`) }))}
              onChange={setView}
            />
            <HpButton variant="secondary" icon="database" onClick={() => void navigate('/marketing/performance/data')}>
              {t('dashboard.dataLink')}
            </HpButton>
            <HpButton variant="secondary" icon="link" onClick={() => void navigate('/marketing/performance/mappings')}>
              {t('dashboard.mappingsLink')}
            </HpButton>
          </span>
        }
      />

      <FilterBar
        filter={filter}
        onChange={setFilter}
        onReset={() => setFilter(defaultFilter(today))}
        options={options.data}
        today={today}
        active={isFilterActive(filter, today)}
      />
      <ErrorAlert error={report.error ?? options.error} />
      {data && !data.previousComplete && data.previous && (
        <p className="pa-filter-notice">
          {t('dashboard.filters.previousIncomplete', { since: options.data?.since ?? '' })}
        </p>
      )}
      {filter.projects.length > 0 && <p className="pa-filter-notice">{t('dashboard.filters.projectSpendNote')}</p>}
      {data?.previous && data.previousAsOf && (
        <p className="bo-hint">
          {t('dashboard.filters.maturityNote', {
            asOf: fmtDateTime(data.asOf),
            previousAsOf: fmtDateTime(data.previousAsOf),
          })}
        </p>
      )}

      <div className="pa-nav-anchor" aria-hidden="true" ref={anchorRef} />
      <nav className="pa-section-nav" aria-label={t('dashboard.sections.aria')} ref={navRef}>
        {SECTIONS.map((sec) => (
          <button
            key={sec.id}
            type="button"
            onClick={() => {
              setView(sec.view);
              setTimeout(
                () => document.getElementById(sec.id)?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
                50,
              );
            }}
          >
            {t(`dashboard.sections.${sec.id}`)}
          </button>
        ))}
      </nav>

      <section className="pa-intro" id="pa-overview">
        <div>
          <h2>{t(`dashboard.intro.${view}Title`)}</h2>
          <p>{t(`dashboard.intro.${view}Lead`)}</p>
          <p>{t(`dashboard.intro.${view}Text`)}</p>
        </div>
      </section>

      {report.isLoading && !data ? (
        <p className="bo-muted">{t('common:actions.loading')}</p>
      ) : (
        data && (
          <div className="pa-manager-grid hp-page-grid">
            <div className="pa-manager-primary">
              <KpiGrid
                view={view}
                current={data.current}
                previous={data.previous}
                previousComplete={data.previousComplete}
                compareOff={filter.compare === 'none'}
                fmt={fmt}
              />
              {view === 'manager' ? (
                <>
                  <TrendChart report={data.current} fmt={fmt} />
                  <Economics report={data.current} fmt={fmt} />
                  <Funnel report={data.current} fmt={fmt} />
                  <ChannelTable
                    report={data.current}
                    fmt={fmt}
                    exportName={`homeportal-zdroje-${filter.from}-${filter.to}`}
                  />
                </>
              ) : (
                <>
                  <ChannelQuality report={data.current} fmt={fmt} />
                  <ChannelTable
                    report={data.current}
                    fmt={fmt}
                    exportName={`homeportal-zdroje-${filter.from}-${filter.to}`}
                  />
                  <CampaignTable
                    report={data.current}
                    fmt={fmt}
                    exportName={`homeportal-vykonnost-${filter.from}-${filter.to}`}
                  />
                  <DataQuality quality={data.quality} fmt={fmt} from={filter.from} to={filter.to} />
                </>
              )}
            </div>
            <aside className="pa-manager-side" aria-label={t('dashboard.aside.title')}>
              {data.budget && <BudgetCard budget={data.budget} fmt={fmt} />}
              <Insights insights={data.insights} fmt={fmt} />
            </aside>
          </div>
        )
      )}

      <details className="pa-methodology">
        <summary>{t('dashboard.methodology.summary')}</summary>
        <div>
          <p>{t('dashboard.methodology.text')}</p>
        </div>
      </details>
      {data && (
        <footer className="pa-footer">
          <div>
            <strong>HOMEPORTAL ANALYTICS</strong>
            <span>{t('dashboard.footer.snapshot', { id: data.snapshotId, asOf: fmtDateTime(data.asOf) })}</span>
          </div>
          <p>{t('dashboard.footer.sources')}</p>
        </footer>
      )}
    </div>
  );
}
